import React, { useState } from 'react'

import classNames from 'classnames'

interface PasswordInputProps {
  label: string
  placeholder?: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  enterKeyHint?: string
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label,
      placeholder,
      value,
      onChange,
      required = false,
      onKeyDown,
      enterKeyHint,
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false)

    const labelClass = classNames('font-semibold', 'mb-2', 'text-sm')
    const inputContainerClass = classNames('relative', 'w-full')
    const inputClass = classNames(
      'rounded-lg',
      'p-3',
      'w-full',
      'pr-12',
      'border',
      'border-gray-300',
      'focus:ring-2',
      'focus:ring-blue-500',
      'focus:border-transparent',
      'outline-none',
    )
    const iconClass = classNames(
      'absolute',
      'right-3',
      'top-1/2',
      'transform',
      '-translate-y-1/2',
      'cursor-pointer',
      'text-gray-500',
    )

    return (
      <div className="flex flex-col mb-4">
        <label className={labelClass}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className={inputContainerClass}>
          <input
            ref={ref}
            className={inputClass}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            required={required}
            {...(enterKeyHint ? ({ enterKeyHint } as any) : {})}
          />
          <button
            type="button"
            className={iconClass}
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </div>
    )
  },
)

PasswordInput.displayName = 'PasswordInput'

export default PasswordInput
